<template>
  <div ref="root" :style="rootStyle">
    <div
      :class="[ns.b(), state.fixed && !isReset && ns.m('fixed')]"
      :style="stickyStyle"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'
import {
  useEventListener,
  useNamespace,
  useRect,
  useScrollParent,
  useVisibilityChange,
} from '@xzx-design/hooks'
import {
  extend,
  getScrollTop,
  getZIndexStyle,
  isHidden,
  unitToPx,
  windowHeight,
  windowWidth,
} from '@xzx-design/utils'
import { stickyEmits, stickyProps } from './sticky'

import type { CSSProperties } from 'vue'

defineOptions({
  name: 'XzxSticky',
})

const props = defineProps(stickyProps)
const emits = defineEmits(stickyEmits)

const ns = useNamespace('sticky')

const root = ref<HTMLElement>()
const scrollParent = useScrollParent(root)
const state = reactive({
  fixed: false,
  width: 0, // root width
  height: 0, // root height
  transform: 0,
})
const isReset = ref(false)

const offset = computed(() =>
  unitToPx(props.position === 'top' ? props.offsetTop : props.offsetBottom)
)

// eslint-disable-next-line vue/return-in-computed-property
const rootStyle = computed<CSSProperties | undefined>(() => {
  if (isReset.value) {
    return
  }
  const { fixed, height, width } = state
  if (fixed) {
    return {
      width: `${width}px`,
      height: `${height}px`,
    }
  }
})

const stickyStyle = computed<CSSProperties | undefined>(() => {
  if (!state.fixed || isReset.value) {
    return
  }

  const style: CSSProperties = extend(getZIndexStyle(props.zIndex), {
    width: `${state.width}px`,
    height: `${state.height}px`,
    [props.position]: `${offset.value}px`,
  })

  if (state.transform) {
    style.transform = `translate3d(0, ${state.transform}px, 0)`
  }

  return style
})

const emitScroll = (scrollTop: number) =>
  emits('scroll', {
    scrollTop,
    isFixed: state.fixed,
  })

const onScroll = () => {
  if (!root.value || isHidden(root)) {
    return
  }

  const { container, position } = props
  const rootRect = useRect(root)
  const scrollTop = getScrollTop(window)

  state.width = rootRect.width
  state.height = rootRect.height

  if (position === 'top') {
    // The sticky component should be kept inside the container element
    if (container) {
      const containerRect = useRect(container)
      const difference = containerRect.bottom - offset.value - state.height
      state.fixed = offset.value > rootRect.top && containerRect.bottom > 0
      state.transform = difference < 0 ? difference : 0
    } else {
      state.fixed = offset.value > rootRect.top
    }
  } else {
    const { clientHeight } = document.documentElement
    if (container) {
      const containerRect = useRect(container)
      const difference =
        clientHeight - containerRect.top - offset.value - state.height
      state.fixed =
        clientHeight - offset.value < rootRect.bottom &&
        clientHeight > containerRect.top
      state.transform = difference < 0 ? -difference : 0
    } else {
      state.fixed = clientHeight - offset.value < rootRect.bottom
    }
  }

  emitScroll(scrollTop)
}

watch(
  () => state.fixed,
  (value) => emits('change', value)
)

useEventListener('scroll', onScroll, {
  target: scrollParent,
  passive: true,
})
useVisibilityChange(root, onScroll)

watch([windowWidth, windowHeight], () => {
  if (!root.value || isHidden(root) || !state.fixed) {
    return
  }
  isReset.value = true
  nextTick(() => {
    const rootRect = useRect(root)
    state.width = rootRect.width
    state.height = rootRect.height
    isReset.value = false
  })
})

// init here
</script>
